import { Link } from "react-router-dom";
import "./managerlogixLanding.css";

const FEATURE_ITEMS = [
  {
    title: "Inspections",
    text: "Move-in and move-out inspections with sharable PDFs, clean history, and audit-ready records.",
  },
  {
    title: "Lease Packages",
    text: "Generate, store, and send lease packages with jurisdiction-aware structure and consistent workflows.",
  },
  {
    title: "Documents",
    text: "Keep insurance, tax, appraisal, financing, and inspection documents in one property-scoped system.",
  },
  {
    title: "Automated Communication",
    text: "Tenant emails and manager routing are built into the workflow so nothing gets missed.",
  },
  {
    title: "Portfolio Views",
    text: "Managers see all assigned properties under one login with role-aware access controls.",
  },
  {
    title: "White-Label Ready",
    text: "Use your branding on generated PDFs and outputs so the platform reflects your business.",
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    title: "Add your property",
    text: "Set jurisdiction, address, and suites so the system can run clean workflows from day one.",
  },
  {
    title: "Invite your team",
    text: "Managers and staff use one login and only see the properties and actions they are allowed to access.",
  },
  {
    title: "Run operations",
    text: "Inspections, leases, work orders, financial tracking, and documents all run from one consistent app.",
  },
  {
    title: "Scale cleanly",
    text: "Add properties, staff, and workflows without rebuilding your process or jumping between tools.",
  },
];

const INCLUDED_ITEMS = [
  "Unlimited inspections",
  "Lease workflows and templates",
  "Multi-property manager portfolios",
  "Documents quick-reference summaries",
  "Email routing automation",
  "White-label friendly outputs",
];

const FAQ_ITEMS = [
  {
    q: "Do managers need separate logins per building?",
    a: "No. One login can access all assigned properties with role-based permissions.",
  },
  {
    q: "Can tenants still log in separately?",
    a: "Yes. Tenant login remains available and unchanged from the current app workflow.",
  },
  {
    q: "Is this replacing the app dashboard?",
    a: "No. This is only the public landing page. The existing app login, routes, and features stay the same.",
  },
  {
    q: "Can we start with one property and expand later?",
    a: "Yes. The app is designed to scale from single properties to larger portfolios.",
  },
];

export default function ManagerlogixLanding() {
  return (
    <div className="mlx-home">
      <header className="mlx-nav">
        <div className="mlx-container mlx-nav-inner">
          <a href="#top" className="mlx-brand" aria-label="ManagerLogix home">
            <img src="/managerlogix.png" alt="ManagerLogix" />
            <span>ManagerLogix</span>
          </a>

          <nav className="mlx-nav-links" aria-label="Marketing sections">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <a href="#about">About</a>
            <a href="#contact">Contact</a>
          </nav>

          <div className="mlx-nav-actions">
            <Link to="/tenant-login" className="mlx-btn mlx-btn-secondary">
              Tenant Login
            </Link>
            <Link to="/manager-login" className="mlx-btn mlx-btn-primary">
              Manager Login
            </Link>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="mlx-hero">
          <div className="mlx-container mlx-hero-grid">
            <div>
              <div className="mlx-badge">
                Built for property managers • Multi-property ready • Jurisdiction-aware
              </div>

              <h1 className="mlx-h1">
                Property management software built for real operators
              </h1>

              <p className="mlx-lead">
                ManagerLogix helps property managers run inspections, leases, documents,
                and multi-property portfolios from one clean, reliable system.
              </p>

              <div className="mlx-hero-actions">
                <Link to="/manager-login" className="mlx-btn mlx-btn-primary mlx-btn-lg">
                  Log In
                </Link>
                <a href="#features" className="mlx-btn mlx-btn-secondary mlx-btn-lg">
                  Explore Features
                </a>
                <Link to="/tenant-login" className="mlx-btn mlx-btn-ghost mlx-btn-lg">
                  Tenant Access
                </Link>
              </div>

              <div className="mlx-chip-row" aria-label="Product highlights">
                <span className="mlx-chip">Per-suite pricing</span>
                <span className="mlx-chip">Audit trails</span>
                <span className="mlx-chip">Manager portfolios</span>
                <span className="mlx-chip">White-label ready</span>
              </div>
            </div>

            <div className="mlx-panel" aria-label="Product preview summary">
              <div className="mlx-panel-brand">
                <img src="/managerlogix.png" alt="" aria-hidden="true" />
                <div>
                  <div className="mlx-panel-title">ManagerLogix</div>
                  <div className="mlx-panel-subtitle">Property management, simplified</div>
                </div>
              </div>

              <div className="mlx-preview-list">
                <PreviewRow
                  title="Move-in Inspection"
                  text="Completed • PDF sent • Audit trail stored"
                  tag="Automated"
                />
                <PreviewRow
                  title="Lease Package"
                  text="Jurisdiction-aware template selected"
                  tag="Smart"
                />
                <PreviewRow
                  title="Documents"
                  text="Insurance & tax summaries visible at a glance"
                  tag="New"
                />
                <PreviewRow
                  title="Portfolio Dashboard"
                  text="One login • Multiple properties • Role-aware access"
                  tag="Unified"
                />
              </div>

              <div className="mlx-toast">
                "Finally, software that feels built for operators, not just reporting."
              </div>
            </div>
          </div>
        </section>

        <section className="mlx-strip" aria-label="Who ManagerLogix is for">
          <div className="mlx-container">
            <div className="mlx-strip-grid">
              <div className="mlx-strip-card">Multi-property managers</div>
              <div className="mlx-strip-card">Owner-operators</div>
              <div className="mlx-strip-card">PM firms scaling operations</div>
              <div className="mlx-strip-card">Cross-jurisdiction portfolios</div>
            </div>
          </div>
        </section>

        <section id="features" className="mlx-section">
          <div className="mlx-container">
            <div className="mlx-section-head">
              <span className="mlx-eyebrow">Features</span>
              <h2>Everything you need, nothing you do not</h2>
              <p>
                ManagerLogix is focused on the workflows that actually run a portfolio:
                inspections, leases, documents, staff access, and operational visibility.
              </p>
            </div>

            <div className="mlx-grid mlx-grid-3">
              {FEATURE_ITEMS.map((item) => (
                <FeatureCard key={item.title} {...item} />
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="mlx-section mlx-section-muted">
          <div className="mlx-container">
            <div className="mlx-section-head">
              <span className="mlx-eyebrow">How It Works</span>
              <h2>Set up once, then run repeatable workflows</h2>
              <p>
                The system is designed so your team follows the same process across
                properties without extra tools or manual cleanup.
              </p>
            </div>

            <div className="mlx-grid mlx-grid-2">
              {HOW_IT_WORKS_STEPS.map((step, idx) => (
                <StepCard key={step.title} index={idx + 1} {...step} />
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="mlx-section">
          <div className="mlx-container">
            <div className="mlx-section-head">
              <span className="mlx-eyebrow">Pricing</span>
              <h2>Simple pricing that scales with your suites</h2>
              <p>
                No tier traps. No per-click fees. Built to stay predictable as your
                portfolio grows.
              </p>
            </div>

            <div className="mlx-grid mlx-grid-2 mlx-pricing-grid">
              <div className="mlx-card mlx-pricing-card">
                <div className="mlx-tag">Most popular</div>
                <div className="mlx-price-wrap">
                  <div className="mlx-price-amount">$2.75</div>
                  <div className="mlx-price-unit">/ suite / month</div>
                </div>
                <p className="mlx-card-copy">
                  One clean plan built to beat legacy platforms on speed, clarity, and workflow.
                </p>

                <div className="mlx-divider" />

                <ul className="mlx-checklist" aria-label="Included features">
                  {INCLUDED_ITEMS.map((item) => (
                    <li key={item}>
                      <CheckIcon />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mlx-card-actions">
                  <Link to="/manager-login" className="mlx-btn mlx-btn-primary">
                    Log In
                  </Link>
                  <a href="#contact" className="mlx-btn mlx-btn-secondary">
                    Contact Sales
                  </a>
                </div>

                <p className="mlx-note">
                  Enterprise options and minimums can be discussed for large portfolios.
                </p>
              </div>

              <div className="mlx-card mlx-card-soft">
                <h3 className="mlx-card-title">FAQ</h3>
                <div className="mlx-divider" />
                <div className="mlx-faq-list">
                  {FAQ_ITEMS.map((item) => (
                    <FaqItem key={item.q} {...item} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="mlx-section mlx-section-muted">
          <div className="mlx-container">
            <div className="mlx-section-head">
              <span className="mlx-eyebrow">About</span>
              <h2>Built to remove friction from property operations</h2>
              <p>
                ManagerLogix was built to reduce the operational drag of inspections,
                leases, communication, and portfolio visibility without the bloat of legacy systems.
              </p>
            </div>

            <div className="mlx-grid mlx-grid-2">
              <div className="mlx-card">
                <h3 className="mlx-card-title">Our philosophy</h3>
                <p className="mlx-card-copy">
                  If a workflow takes more steps than it should, it is a bug. We build
                  for speed, clarity, and consistency across properties and jurisdictions.
                </p>
              </div>
              <div className="mlx-card">
                <h3 className="mlx-card-title">Who it is for</h3>
                <p className="mlx-card-copy">
                  Professional property managers, owner-operators, and teams that want
                  a modern UI, reliable records, and scalable portfolio workflows.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="mlx-section">
          <div className="mlx-container">
            <div className="mlx-cta-card">
              <div className="mlx-cta-copy">
                <span className="mlx-eyebrow">Get Started</span>
                <h2>Ready to run your portfolio smarter?</h2>
                <p>
                  Use the existing Manager Login to access the app. Tenant access remains
                  available through the Tenant Login flow.
                </p>
              </div>

              <div className="mlx-cta-actions">
                <Link to="/manager-login" className="mlx-btn mlx-btn-primary mlx-btn-lg">
                  Manager Login
                </Link>
                <Link to="/tenant-login" className="mlx-btn mlx-btn-secondary mlx-btn-lg">
                  Tenant Login
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="mlx-footer">
        <div className="mlx-container mlx-footer-grid">
          <div>
            <div className="mlx-brand mlx-brand-footer">
              <img src="/managerlogix.png" alt="ManagerLogix" />
              <span>ManagerLogix</span>
            </div>
            <p className="mlx-footer-copy">
              Modern property management software for inspections, leases, documents,
              and multi-property operations.
            </p>
          </div>

          <div className="mlx-footer-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <a href="#about">About</a>
          </div>

          <div className="mlx-footer-links">
            <Link to="/manager-login">Manager Login</Link>
            <Link to="/tenant-login">Tenant Login</Link>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PreviewRow({ title, text, tag }) {
  return (
    <div className="mlx-preview-row">
      <div className="mlx-preview-row-copy">
        <div className="mlx-preview-row-title">{title}</div>
        <div className="mlx-preview-row-text">{text}</div>
      </div>
      <span className="mlx-preview-tag">{tag}</span>
    </div>
  );
}

function FeatureCard({ title, text }) {
  return (
    <article className="mlx-card">
      <div className="mlx-feature-icon">
        <BoltIcon />
      </div>
      <h3 className="mlx-card-title">{title}</h3>
      <p className="mlx-card-copy">{text}</p>
    </article>
  );
}

function StepCard({ index, title, text }) {
  return (
    <article className="mlx-card">
      <div className="mlx-step-badge">Step {index}</div>
      <h3 className="mlx-card-title">{title}</h3>
      <p className="mlx-card-copy">{text}</p>
    </article>
  );
}

function FaqItem({ q, a }) {
  return (
    <div className="mlx-faq-item">
      <h4>{q}</h4>
      <p>{a}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M13 2L3 14h7l-1 8 12-14h-7l-1-6z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
